import * as Sentry from '@sentry/react';
import { ErrorBoundaryProps } from '@sentry/react';
import { SentryTeams } from '@mews/enums';
import { SentryTags } from './types';

interface Props extends ErrorBoundaryProps {
    beforeCapture?: ((scope: Sentry.Scope, error: unknown, componentStack: string | undefined) => void) | undefined;
    team?: SentryTeams;
}

// Additional handler for events like 'click' or 'fetch' cases, where React error boundary doesn't catch.
export const captureWithSentry =
    <T, R>({
        fn,
        onError,
        team,
    }: {
        fn: (event?: T) => Promise<R> | R;
        onError?: (error: Error) => void;
        team: SentryTeams;
    }) =>
    async (event?: T): Promise<R | undefined> => {
        try {
            return await fn(event);
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setTag(SentryTags.Team, team);
                Sentry.captureException(typeof error === 'string' ? error : error.message);
            });
            onError?.(error);
            // eslint-disable-next-line no-console
            console.error(error);
            return undefined;
        }
    };

export const SentryCustomErrorBoundary = ({
    beforeCapture,
    children,
    team,
    ...props
}: React.PropsWithChildren<Props>) => (
    <Sentry.ErrorBoundary
        {...props}
        beforeCapture={(scope, error, componentStack) => {
            beforeCapture?.(scope, error, componentStack);
            scope.setTag(SentryTags.Team, team);
        }}
    >
        {children}
    </Sentry.ErrorBoundary>
);
